/**
 * user-validation.js
 */

import { matchPath } from "react-router";
import { Types } from "./constants";
import { renderForMobileContext } from "./app-context";
import { paths, recordListPaths } from "./routing";
import { Link } from "react-router-dom";

// PT1-RETIRE: remove pt1 routes

const billingPaths = [
  '/billing',
  '/billing/edit'
];
const profilePaths = [
  '/profile',
  '/profile/edit/details',
  '/profile/edit/notifications',
  '/profile/edit/photo',
  '/profile/edit/docs',
  '/profile/edit/password'
];
const miscPaths = [
  '/support',
  '/tutorials',
  '/user-guide',
];
const memberRequestPaths = [  
  '/manage/request/:code'
];
const limitedPaths = [
  '/records',
  '/home',
  // training drawer routes
  ...recordListPaths.map(r => `${r}${paths.training.base}${paths.training.pattern}/(answer)?/:answerId?`),
  // deployment drawer routes
  ...recordListPaths.map(r => `${r}${paths.deployment.base}${paths.deployment.pattern}`),
  // class drawer routes
  ...recordListPaths.map(r => `${r}${paths.class.base}${paths.class.pattern}`),
];


const validatePath = (intendedPath, validPaths) => {
  return Boolean(matchPath(intendedPath, { path: validPaths, exact: true }));
}


export function validateLocation(user, dogs, admin, intendedPath) {
  if (!user || !dogs || (admin && intendedPath.startsWith('/admin'))) {
    return;
  }

  const handlerSubscription = user
    .subscription
    .subscriptions
    .find(s => s.role.id === Types.Role.HANDLER);
  intendedPath = intendedPath.toLowerCase();
  let validPaths = [];

  // UNACTIVATED / SUSPENDED / NO SUBSCRIPTION
  if (user.role.isHandler && 
    (
      user.role.status.isUnactivated 
      || user.role.status.isSuspended
      || (
        !(user.role.status.isActive || user.role.status.isLimited)
        && !handlerSubscription 
        && !user.subscription.isBypassed
      )
    )
  ) {
    validPaths = [
      ...billingPaths,
      ...profilePaths,
      ...miscPaths,
    ];

    if (!validatePath(intendedPath, validPaths)) {
      return { route: "/billing/edit" };
    }
  }

  // PROFILE INCOMPLETE
  else if (!user.departmentName) {
    validPaths = [
      ...billingPaths,
      ...profilePaths,
      ...miscPaths
    ];

    if (!validatePath(intendedPath, validPaths)) {
      return { location: "/profile" };
    }
  }

  // LIMITED & NO DOGS (rare but unique case - basic and billing only)
  else if (user.role.status.isLimited && user.role.isHandler && dogs.length === 0) {
    validPaths = [
      ...billingPaths,
      ...profilePaths,
      ...miscPaths
    ];

    if (!validatePath(intendedPath, validPaths)) {
      return { location: '/billing' };
    }
  }

  // NO DOGS
  else if (user.role.isHandler && dogs.length === 0) {
    validPaths = [
      ...billingPaths,
      ...profilePaths,
      ...miscPaths,
      ...memberRequestPaths,
      "/dogs",
      "/dogs/add/details"
    ];

    if (!validatePath(intendedPath, validPaths)) {
      return { route: "/dogs/add/details" };
    }
  }

  // LIMITED
  else if (user.role.status.isLimited) {
    validPaths = [
      '/',
      ...billingPaths,
      ...profilePaths,
      ...miscPaths,
      ...recordListPaths,
      ...limitedPaths,
    ];

    if (!validatePath(intendedPath, validPaths)) {
      if (renderForMobileContext()) {
        return { location: '/' };
      } else {
        return { route: '/records' };
      }
    }
  }
}


export function getNotifications(user, dogs, activeDogs) {
  if (user && dogs) {
    const handlerSubscription = user
      .subscription
      .subscriptions
      .find(s => s.role.id === Types.Role.HANDLER);

    // handler role with no sub
    if (user.role.isHandler && !user.subscription.isBypassed && !handlerSubscription) {
      return {
        id: 'no-subscription',
        message: 'Your account is not active. Please create a subscription to continue.',
        variant: 'warning',
        important: true
      }
    }
    // using app without active role
    else if (renderForMobileContext() && !user.role.status.isActive) {
      return {
        id: 'no-subscription-app',
        message: 'Your account is not active. Please log into the website at mypacktrack.com for more information.',
        variant: 'warning',
        important: true
      }
    }
    // user profile incomplete
    else if (!user.departmentName) {
      return {
        id: 'incomplete-profile',
        message: 'Your user profile is incomplete. Please update it now.',
        variant: 'info',
        important: true
      }
    } 
    // limited and no dogs
    else if (user.role.status.isLimited && user.role.isHandler && dogs.length === 0) {
      return {
        id: 'limited-no-dogs',
        message: <div>This account is inactive and you have no dogs. <br />Reactivate your subscription on the <Link to={billingPaths[0]} >Billing</Link> page</div>,
        variant: 'warning',
        important: true,
      }
    }
    // user has no dogs
    else if (user.role.isHandler && dogs.length === 0) {
      return {
        id: 'no-dogs',
        message: 'You must create a dog profile in order to use PACKTRACK. Please do this now.',
        variant: 'info',
        important: true
      };
    }
    // user has no active dogs
    else if (user.role.isHandler && activeDogs.length === 0) {
      return {
        id: 'no-active-dogs',
        message: 'You have no active dogs. To create new records you must have at least one active dog.',
        variant: 'warning',
        important: false
      };
    }
    // limited status - subscription expired
    else if (user.role.status.isLimited) {
      return {
        id: 'limited-role',
        message: <div>This account is inactive but you can still view records and create reports. Reactivate your subscription on the <Link to={billingPaths[0]} >Billing</Link> page</div>,
        variant: 'warning',
        important: true
      }
    }
    // no notification to display
    else {
      return false;
    }
  }
}